import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography, Grid, useMediaQuery } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

// UTILITY
import { analyticsClickedAppToFilterContent } from '@/utility/analytics';
import store from '@/utility/store';

// HOOKS
import useActions from '@/hooks/useActions';

// STORE
import { OnboardingActions } from '@/store/actions';

// COMPONENTS
import SectionTitleLink from '@/components/SectionTitleLink';
import StageSwiperCarousel from '@/components/Carousels/StageSwiperCarousel';
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';
import CheckBoxCard from '@/components/Form/CheckBoxCard';

// STYLES
import { Container, CarouselWrapper } from './styles';

const swiperCarouselOptions = {
  navigation: false,
  breakpoints: {
    0: {
      spaceBetween: 16,
      slidesPerView: 'auto',
    },
    1200: {
      speed: 1000,
      slidesPerView: 9,
      slidesPerGroup: 1,
      spaceBetween: 16,
    },
  },
};

const SelectAppsCarousel = props => {
  const {
    data,
    title,
    subtitle,
    showSeeAll,
    loading,
    analyticsLocation,
    hideTitle,
    onboardingCallback,
    onSelectApp,
  } = props;

  const [t] = useTranslation();
  const { selectedApps } = useSelector(({ onboarding }) => ({
    selectedApps: onboarding.selectedMyApps,
  }));
  const [onboardingSetState, onboardingUpdateSelectedApps] = useActions([
    (onbState, onbProps) =>
      OnboardingActions.onboardingSetState.action({ state: onbState, props: onbProps }),
    apps => OnboardingActions.onboardingUpdateSelectedApps.action({ data: apps }),
  ]);
  const downSm = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));

  const skeletons = new Array(downSm ? 3 : 6).fill('');
  const skeletonsSection = (
    <>
      {!hideTitle && (
        <Grid item xs={12}>
          <Skeleton width="40%" height={50} />
          <Skeleton width="40%" height={30} />
        </Grid>
      )}
      {skeletons.map((item, index) => {
        return (
          <Grid item xs={4} md={2} key={index}>
            <Skeleton variant="rect" height={88} />
          </Grid>
        );
      })}
    </>
  );

  const selectedAppsIds = selectedApps.map(app => app.id);

  function handleSelectApp(appData, analyticsProperties) {
    let updatedSelectedApps = [...selectedApps];
    const hasApp = !!selectedApps.find(app => app.id === appData.id);

    if (hasApp) {
      updatedSelectedApps = updatedSelectedApps.filter(app => app.id !== appData.id);
    } else {
      updatedSelectedApps.push(appData);
    }

    const properties = {
      app_name: appData.name,
      app_id: appData.id,
      app_index: analyticsProperties.card_index,
      app_selected: Number(hasApp),
    };

    analyticsClickedAppToFilterContent(properties);

    onboardingUpdateSelectedApps(updatedSelectedApps);
    store.set('my_apps', updatedSelectedApps);

    onSelectApp(appData);
  }

  function handleOpenOnboarding() {
    onboardingSetState(true, {
      callback: onboardingCallback,
    });
  }

  return (
    <Container className={clsx('carousel-select-apps')}>
      {loading ? (
        <Grid container spacing={2}>
          {skeletonsSection}
        </Grid>
      ) : (
        <>
          {data.length ? (
            <>
              {!!title && !hideTitle && (
                <div className="carousel-select-apps__title-wrap">
                  <SectionTitleLink
                    className="carousel-select-apps__title"
                    title={title}
                    analyticsLocation={analyticsLocation}
                    link=" "
                    LinkProps={{ component: 'h4', onClick: handleOpenOnboarding }}
                  />
                  {subtitle && (
                    <Typography className={clsx(downSm && 'p4')} variant="subtitle1">
                      {subtitle}
                    </Typography>
                  )}
                </div>
              )}
              <CarouselWrapper>
                <StageSwiperCarousel carouselOptions={swiperCarouselOptions}>
                  {data.map((item, card_index) => (
                    <SwiperSlide key={card_index}>
                      <div className="card-wrapper">
                        <CheckBoxCard
                          name="select-apps"
                          value={item.id}
                          imageSrc={item.thumbnail}
                          handleChange={() => handleSelectApp(item, { card_index })}
                          checkedValue={selectedAppsIds}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                  {showSeeAll && data.length >= 6 && (
                    <SwiperSlide>
                      <div className="card-wrapper--see-all">
                        <div className="dummy-card">
                          <div
                            className="dummy-card__content"
                            onClick={handleOpenOnboarding}
                            location={analyticsLocation}
                            role="presentation"
                          >
                            <i className="icon-arrow-right" />
                            <Typography className="p4" component="span">
                              {t('buttons.seeAll')}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )}
                </StageSwiperCarousel>
              </CarouselWrapper>
            </>
          ) : null}
        </>
      )}
    </Container>
  );
};

SelectAppsCarousel.defaultProps = {
  data: [],
  title: '',
  subtitle: '',
  showSeeAll: false,
  loading: false,
  onSelectApp: () => {},
  onboardingCallback: () => {},
  hideTitle: false,
  hideMobileNav: false,
};

SelectAppsCarousel.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  analyticsLocation: PropTypes.string,
  showSeeAll: PropTypes.bool,
  loading: PropTypes.bool,
  onSelectApp: PropTypes.func,
  onRemoveStatus: PropTypes.func,
  hideTitle: PropTypes.bool,
  hideMobileNav: PropTypes.bool,
  user: PropTypes.object,
  onboardingCallback: PropTypes.func,
};

export default React.memo(SelectAppsCarousel);
